/**
 * User-tunable visual settings. Live in localStorage so they survive reloads.
 * Mutated directly by SettingsPanel; consumers (ChaseCamera, PostFx) read each frame.
 */
export interface VisualSettings {
  /** Camera field of view in degrees. 50..110, default 52 (matches the legacy 0.9 rad). */
  fovDegrees: number;
  /** Add an automatic FoV widening on top of the base FoV that scales with speed. */
  speedFovBoost: boolean;
  /**
   * Derive camera distance from FoV (50° → 10 m, 100° → 5 m) so the car stays a similar
   * size on screen. When on, the distance slider is driven by the FoV slider.
   */
  linkFovDistance: boolean;
  /** Whether to attach the MotionBlurPostProcess. */
  motionBlurEnabled: boolean;
  /** Motion blur intensity. 0..2, default 1. */
  motionBlurStrength: number;
  /** Whether the camera shakes from hard accel/decel/crashes. */
  cameraShakeEnabled: boolean;
  /** Shake intensity multiplier. 0..2, default 1. */
  cameraShakeIntensity: number;
  /** Whether the camera lags behind the car during acceleration. */
  cameraLagEnabled: boolean;
  /** Baseline camera distance behind the car, in meters. 4..15, default 7. */
  cameraDistance: number;
  /** Camera height above the car, in meters. 1..6, default 3. */
  cameraHeight: number;
  /**
   * How much camera distance varies with acceleration. 0 = always baseline (no variation),
   * 1 = full effect (standstill closer, hard accel further), 2 = double the variation.
   */
  accelDistanceAmount: number;
  /**
   * Radial "speed blur" — screen-space blur that fades outward from screen center,
   * scaled by speed. 0 = off, 2 = strong. Cheap shader, no gbuffer pass.
   */
  radialBlurIntensity: number;
  /**
   * Engine power multiplier applied to the vehicle's throttle force. 1.0 = full
   * (original behavior, ~300+ km/h top speed); 0.5 = half power & roughly half top
   * speed. Brakes are not scaled.
   */
  carPower: number;
  /**
   * Use the "classic" (pre-visual-overhaul) color palette: darker tree foliage, a
   * deeper mossy terrain green, and a more saturated blue sky/fog. False = the
   * pastel cartoon look introduced by the visual overhaul. Read once at startup +
   * baked into materials, so flipping triggers a page reload.
   */
  useClassicColors: boolean;
  /**
   * Multiplier applied to the active palette's fogStart + fogEnd. 1.0 = palette default.
   * Push higher to extend draw distance (distant terrain stays visible against the sky);
   * push lower to render less geometry per frame. Live-updatable — no reload required.
   */
  fogDistanceMul: number;
  /** Diagnostic bisect toggles (see SettingsPanel → Diagnostics). */
  diagShadowsEnabled: boolean;
  diagPostFxEnabled: boolean;
  diagSkyEnabled: boolean;
  diagTerrainShadingEnabled: boolean;
  diagVehicleFxEnabled: boolean;
  diagCameraModsEnabled: boolean;
  /** Render the near-field 3D grass blades that follow the car. Default true. The
   *  terrain-detail grass shading is independent of this and always on. */
  grassEnabled: boolean;
  /** Anime/cel look: a screen-space post-process that draws bold black ink outlines along
   *  depth silhouettes and posterizes lighting into flat bands. Default true. Requires
   *  post-processing to be enabled. */
  animeStyleEnabled: boolean;
  /** When true, the bottom-left HUD shows the perf overlay (FPS / GPU ms / Draws /
   *  Tris / LOD pill). When false those rows are hidden but still updated, so
   *  flipping back on shows current values. Default false — the perf overlay is
   *  developer-flavored info, not something the average player wants on screen. */
  showDebugOverlay: boolean;
  /** Photo mode — hides the minimap, location bar, delivery card, and delivery pills
   *  for a clean screenshot. Toggleable via Settings or the `P` hotkey. */
  photoMode: boolean;
}

export const DEFAULT_SETTINGS: Readonly<VisualSettings> = Object.freeze({
  fovDegrees: 70,
  speedFovBoost: true,
  linkFovDistance: false,
  motionBlurEnabled: false,
  motionBlurStrength: 1.0,
  cameraShakeEnabled: true,
  cameraShakeIntensity: 0.2,
  cameraLagEnabled: true,
  cameraDistance: 5.5,
  cameraHeight: 3.0,
  accelDistanceAmount: 0.8,
  radialBlurIntensity: 0.3,
  carPower: 0.65,
  useClassicColors: false,
  fogDistanceMul: 1.0,
  diagShadowsEnabled: true,
  diagPostFxEnabled: true,
  diagSkyEnabled: true,
  diagTerrainShadingEnabled: true,
  diagVehicleFxEnabled: true,
  diagCameraModsEnabled: false,
  grassEnabled: true,
  animeStyleEnabled: true,
  showDebugOverlay: false,
  photoMode: false,
});

const STORAGE_KEY = 'roadworld.visualSettings.v1';

export function loadSettings(): VisualSettings {
  if (typeof localStorage === 'undefined') return { ...DEFAULT_SETTINGS };
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return { ...DEFAULT_SETTINGS };
  try {
    const parsed = JSON.parse(raw) as Partial<VisualSettings>;
    // Merge over defaults so new fields work after a settings-format extension.
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

let saveTimer: number | null = null;

/** Debounced save. Repeated calls within 300ms collapse to one write. */
export function saveSettings(settings: VisualSettings): void {
  if (typeof localStorage === 'undefined') return;
  if (saveTimer !== null) clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Quota exceeded or storage unavailable — silently drop.
    }
    saveTimer = null;
  }, 300);
}

/**
 * Synchronously persist settings, bypassing the 300ms debounce. Use before a forced
 * reload (e.g. the classic-colors toggle) so the freshly-changed value is on disk
 * before the new page boots.
 */
export function flushSettings(settings: VisualSettings): void {
  if (typeof localStorage === 'undefined') return;
  if (saveTimer !== null) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Quota exceeded or storage unavailable — silently drop.
  }
}

export function resetSettings(target: VisualSettings): void {
  Object.assign(target, DEFAULT_SETTINGS);
}

const CAMERA_DISTANCE_MIN = 4;
const CAMERA_DISTANCE_MAX = 15;

/** Calibration anchors for FoV ↔ distance linking (linear between these points). */
const FOV_LINK_FOV_LOW = 50;
const FOV_LINK_FOV_HIGH = 100;
const FOV_LINK_DIST_AT_LOW_FOV = 10;
const FOV_LINK_DIST_AT_HIGH_FOV = 5;

/** Camera distance that keeps apparent car size steady for a given FoV when linking is on. */
export function cameraDistanceForLinkedFov(fovDegrees: number): number {
  const t = (fovDegrees - FOV_LINK_FOV_LOW) / (FOV_LINK_FOV_HIGH - FOV_LINK_FOV_LOW);
  const dist = FOV_LINK_DIST_AT_LOW_FOV + t * (FOV_LINK_DIST_AT_HIGH_FOV - FOV_LINK_DIST_AT_LOW_FOV);
  return Math.min(CAMERA_DISTANCE_MAX, Math.max(CAMERA_DISTANCE_MIN, dist));
}

export function applyLinkedCameraDistance(settings: VisualSettings): void {
  if (!settings.linkFovDistance) return;
  settings.cameraDistance = cameraDistanceForLinkedFov(settings.fovDegrees);
}
